import React, { useState, useEffect, useCallback } from 'react';
import { adminApiService } from '../../services/adminApiService';
import { FaCheckCircle, FaTimesCircle, FaSyncAlt, FaToggleOn, FaToggleOff, FaInfoCircle, FaChartLine, FaList, FaDollarSign } from 'react-icons/fa';
import './AdminComponents.css';

interface MatchingEngineOrder {
  id: number;
  userId: number;
  symbol: string;
  side: string;
  type: string;
  price?: number;
  quantity: number;
  filledQuantity: number;
  remainingQuantity: number;
  status: string;
  createdAt: string;
  updatedAt: string;
}

interface MatchingEngineTrade {
  id: number;
  buyOrderId: number;
  sellOrderId: number;
  symbol: string;
  price: number;
  quantity: number;
  buyerFee: number;
  sellerFee: number;
  executedAt: string;
}

interface Statistics {
  volume24h: number;
  trades24h: number;
  activeOrders: number;
  averageTradeSize: number;
  totalTrades: number;
}

interface MockDataStatus {
  mockDataEnabled: boolean;
  message: string;
}

/**
 * Matching Engine Management Component for Admin
 * Displays orders, trades, statistics, and allows mock data toggle
 */
const MatchingEngineManagement: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  
  // Orders state
  const [orders, setOrders] = useState<MatchingEngineOrder[]>([]);
  const [orderFilter, setOrderFilter] = useState({ symbol: '', status: '', userId: '' });
  
  // Trades state
  const [trades, setTrades] = useState<MatchingEngineTrade[]>([]);
  const [tradeFilter, setTradeFilter] = useState({ symbol: '', userId: '', from: '', to: '' });
  
  // Statistics state
  const [statistics, setStatistics] = useState<Statistics | null>(null);
  
  // Mock data state
  const [mockDataStatus, setMockDataStatus] = useState<MockDataStatus | null>(null);
  
  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [ordersResponse, tradesResponse, statsResponse, mockStatusResponse] = await Promise.all([
        adminApiService.get<MatchingEngineOrder[]>('/api/admin/matching-engine/orders'),
        adminApiService.get<MatchingEngineTrade[]>('/api/admin/matching-engine/trades'),
        adminApiService.get<Statistics>('/api/admin/matching-engine/statistics'),
        adminApiService.get<MockDataStatus>('/api/admin/matching-engine/mock-data/status')
      ]);

      if (ordersResponse.success && ordersResponse.data) {
        const apiResponse = ordersResponse.data as any;
        setOrders(apiResponse.status === 'Successful' && apiResponse.data ? apiResponse.data : (Array.isArray(apiResponse) ? apiResponse : []));
      }

      if (tradesResponse.success && tradesResponse.data) {
        const apiResponse = tradesResponse.data as any;
        setTrades(apiResponse.status === 'Successful' && apiResponse.data ? apiResponse.data : (Array.isArray(apiResponse) ? apiResponse : []));
      }

      if (statsResponse.success && statsResponse.data) {
        const apiResponse = statsResponse.data as any;
        setStatistics(apiResponse.status === 'Successful' && apiResponse.data ? apiResponse.data : apiResponse);
      }

      if (mockStatusResponse.success && mockStatusResponse.data) {
        const apiResponse = mockStatusResponse.data as any;
        setMockDataStatus(apiResponse.status === 'Successful' && apiResponse.data ? apiResponse.data : apiResponse);
      }
    } catch (err: any) {
      console.error('Error loading matching engine data:', err);
      setError(err.message || 'An unexpected error occurred.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
    // Auto-refresh every 10 seconds
    const interval = setInterval(loadData, 10000);
    return () => clearInterval(interval);
  }, [loadData]);

  const handleToggleMockData = useCallback(async () => {
    if (!mockDataStatus) return;
    
    setLoading(true);
    setError(null);
    setSuccessMessage(null);
    try {
      const response = await adminApiService.post<MockDataStatus>('/api/admin/matching-engine/mock-data/toggle', {
        enabled: !mockDataStatus.mockDataEnabled
      });
      
      if (response.success && response.data) {
        const apiResponse = response.data as any;
        const status = apiResponse.status === 'Successful' && apiResponse.data ? apiResponse.data : apiResponse;
        setMockDataStatus(status);
        setSuccessMessage(`Mock data ${status.mockDataEnabled ? 'enabled' : 'disabled'} successfully!`);
      } else {
        setError(response.error || 'Failed to toggle mock data.');
      }
    } catch (err: any) {
      console.error('Error toggling mock data:', err);
      setError(err.message || 'Failed to toggle mock data.');
    } finally {
      setLoading(false);
    }
  }, [mockDataStatus, loadData]);

  const handleCancelOrder = async (orderId: number) => {
    if (!window.confirm('Are you sure you want to cancel this order?')) {
      return;
    }
    
    setLoading(true);
    setError(null);
    setSuccessMessage(null);
    try {
      const response = await adminApiService.post(`/api/admin/matching-engine/orders/${orderId}/cancel`, {});
      if (response.success) {
        setSuccessMessage('Order cancelled successfully!');
        await loadData();
      } else {
        setError(response.error || 'Failed to cancel order.');
      }
    } catch (err: any) {
      console.error('Error cancelling order:', err);
      setError(err.message || 'Failed to cancel order.');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'PENDING': return 'bg-yellow-100 text-yellow-800';
      case 'PARTIALLY_FILLED': return 'bg-blue-100 text-blue-800';
      case 'FILLED': return 'bg-green-100 text-green-800';
      case 'CANCELLED': return 'bg-gray-100 text-gray-800';
      case 'REJECTED': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="admin-content-panel">
      <h2 className="text-2xl font-bold mb-6 text-gray-800 flex items-center gap-2">
        <FaChartLine className="text-zazoo-accent-blue" />
        Matching Engine Management
      </h2>

      {loading && <div className="loading-indicator">Loading...</div>}
      {error && <div className="error-message text-red-600 mb-4">{error}</div>}
      {successMessage && <div className="success-message text-green-600 mb-4">{successMessage}</div>}

      {/* Mock Data Toggle */}
      <div className="bg-white p-6 rounded-lg shadow-md mb-6">
        <h3 className="text-xl font-semibold mb-4 text-gray-700 flex items-center gap-2">
          <FaToggleOn className="text-zazoo-accent-blue" />
          Mock Data Control
        </h3>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-600 mb-2">
              {mockDataStatus?.mockDataEnabled 
                ? '⚠️ Mock data mode is active. Orders will be simulated without real balance updates.'
                : '✅ Real data mode is active. Orders will execute with real balance updates.'}
            </p>
            <p className="text-xs text-gray-500">{mockDataStatus?.message}</p>
          </div>
          <button
            onClick={handleToggleMockData}
            disabled={loading || !mockDataStatus}
            className={`px-6 py-2.5 rounded-lg text-white font-semibold transition-opacity ${
              mockDataStatus?.mockDataEnabled 
                ? 'bg-red-500 hover:bg-red-600' 
                : 'bg-green-500 hover:bg-green-600'
            } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            {mockDataStatus?.mockDataEnabled ? (
              <>
                <FaToggleOn className="inline-block mr-2" />
                Disable Mock Data
              </>
            ) : (
              <>
                <FaToggleOff className="inline-block mr-2" />
                Enable Mock Data
              </>
            )}
          </button>
        </div>
      </div>

      {/* Statistics */}
      {statistics && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white p-4 rounded-lg shadow-md">
            <div className="flex items-center gap-2 mb-2">
              <FaDollarSign className="text-zazoo-green" />
              <h4 className="text-sm font-semibold text-gray-600">24h Volume</h4>
            </div>
            <p className="text-2xl font-bold text-gray-800">
              ${statistics.volume24h?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}
            </p>
          </div>
          
          <div className="bg-white p-4 rounded-lg shadow-md">
            <div className="flex items-center gap-2 mb-2">
              <FaChartLine className="text-zazoo-accent-blue" />
              <h4 className="text-sm font-semibold text-gray-600">24h Trades</h4>
            </div>
            <p className="text-2xl font-bold text-gray-800">{statistics.trades24h || 0}</p>
          </div>
          
          <div className="bg-white p-4 rounded-lg shadow-md">
            <div className="flex items-center gap-2 mb-2">
              <FaList className="text-zazoo-orange" />
              <h4 className="text-sm font-semibold text-gray-600">Active Orders</h4>
            </div>
            <p className="text-2xl font-bold text-gray-800">{statistics.activeOrders || 0}</p>
          </div>
          
          <div className="bg-white p-4 rounded-lg shadow-md">
            <div className="flex items-center gap-2 mb-2">
              <FaInfoCircle className="text-zazoo-accent-pink" />
              <h4 className="text-sm font-semibold text-gray-600">Avg Trade Size</h4>
            </div>
            <p className="text-2xl font-bold text-gray-800">
              {statistics.averageTradeSize?.toFixed(4) || '0.0000'}
            </p>
          </div>
        </div>
      )}

      {/* Orders Table */}
      <div className="bg-white p-6 rounded-lg shadow-md mb-6">
        <h3 className="text-xl font-semibold mb-4 text-gray-700">Orders</h3>
        
        {/* Filters */}
        <div className="grid grid-cols-3 gap-4 mb-4">
          <input
            type="text"
            placeholder="Filter by symbol"
            value={orderFilter.symbol}
            onChange={(e) => setOrderFilter({ ...orderFilter, symbol: e.target.value })}
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm"
          />
          <select
            value={orderFilter.status}
            onChange={(e) => setOrderFilter({ ...orderFilter, status: e.target.value })}
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm"
          >
            <option value="">All Statuses</option>
            <option value="PENDING">Pending</option>
            <option value="PARTIALLY_FILLED">Partially Filled</option>
            <option value="FILLED">Filled</option>
            <option value="CANCELLED">Cancelled</option>
            <option value="REJECTED">Rejected</option>
          </select>
          <input
            type="number"
            placeholder="Filter by User ID"
            value={orderFilter.userId}
            onChange={(e) => setOrderFilter({ ...orderFilter, userId: e.target.value })}
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm"
          />
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Symbol</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Side</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Price</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Quantity</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Filled</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {orders
                .filter(order => {
                  if (orderFilter.symbol && !order.symbol.includes(orderFilter.symbol)) return false;
                  if (orderFilter.status && order.status !== orderFilter.status) return false;
                  if (orderFilter.userId && order.userId.toString() !== orderFilter.userId) return false;
                  return true;
                })
                .map((order) => (
                  <tr key={order.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{order.id}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{order.userId}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{order.symbol}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        order.side === 'BUY' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {order.side}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{order.type}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {order.price ? order.price.toFixed(8) : 'Market'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{order.quantity.toFixed(4)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {order.filledQuantity.toFixed(4)} / {order.quantity.toFixed(4)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadgeClass(order.status)}`}>
                        {order.status.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(order.createdAt).toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {(order.status === 'PENDING' || order.status === 'PARTIALLY_FILLED') && (
                        <button
                          onClick={() => handleCancelOrder(order.id)}
                          className="text-red-600 hover:text-red-900"
                          title="Cancel Order"
                        >
                          <FaTimesCircle />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Trades Table */}
      <div className="bg-white p-6 rounded-lg shadow-md">
        <h3 className="text-xl font-semibold mb-4 text-gray-700">Recent Trades</h3>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Symbol</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Price</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Quantity</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Buyer Fee</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Seller Fee</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Executed At</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {trades.slice(0, 50).map((trade) => (
                <tr key={trade.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{trade.id}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{trade.symbol}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{trade.price.toFixed(8)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{trade.quantity.toFixed(4)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{trade.buyerFee.toFixed(8)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{trade.sellerFee.toFixed(8)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(trade.executedAt).toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default MatchingEngineManagement;

